import { useEffect } from "react";
import { useAuth } from "../state/AuthContext";
import { sessionActivity } from "../services/sessionActivity";
import { authService } from "../services/authService";

// Same keep-alive interval as reference/ask-the-ai-experts. JWT expiry can
// require an earlier refresh; decoding here is only for scheduling.
const REFRESH_INTERVAL_MS = 15 * 60 * 1000;
const REFRESH_BEFORE_EXPIRY_MS = 120 * 1000;

const refreshDelay = (token: string): number => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    const expiresAt = Number(payload.exp) * 1000;
    if (Number.isFinite(expiresAt) && expiresAt > 0) {
      const remaining = expiresAt - Date.now();
      // Short-lived tokens renew halfway through their remaining lifetime.
      return Math.min(REFRESH_INTERVAL_MS, Math.max(0, remaining - Math.min(REFRESH_BEFORE_EXPIRY_MS, remaining / 2)));
    }
  } catch {
    // The service remains responsible for verifying the token.
  }
  return REFRESH_INTERVAL_MS;
};

const forceLogout = () => {
  const currentPath = window.location.pathname + window.location.search;
  if (currentPath && currentPath !== "/" && currentPath !== "/auth/callback") {
    localStorage.setItem("bri_return_path", currentPath);
  }
  authService.clearAuth();
  authService.redirectToLogin();
};

export const SessionManager = () => {
  const { token, isAuthenticated, refresh } = useAuth();

  useEffect(() => {
    if (!token || !isAuthenticated) return;
    let cancelled = false;
    let inFlight = false;
    let timer: ReturnType<typeof setTimeout>;
    let dueAt = Date.now() + refreshDelay(token);

    const renew = async () => {
      if (cancelled || inFlight) return;
      // A request may have rotated the token before React has rendered it.
      if (authService.getToken() !== token) return;
      inFlight = true;
      clearTimeout(timer);
      try {
        const renewed = await refresh();
        if (cancelled) return;
        if (!renewed) {
          forceLogout();
          return;
        }
        // Usually the token change restarts this effect. Also support a server
        // returning the same token without creating an immediate refresh loop.
        dueAt = Date.now() + Math.max(1000, refreshDelay(renewed));
        timer = setTimeout(() => void renew(), dueAt - Date.now());
      } catch {
        if (!cancelled) forceLogout();
      } finally {
        inFlight = false;
      }
    };
    const checkOnResume = () => {
      if (Date.now() >= dueAt) void renew();
    };
    const offExpired = sessionActivity.onExpired(forceLogout);
    timer = setTimeout(() => void renew(), Math.max(0, dueAt - Date.now()));
    window.addEventListener("focus", checkOnResume);
    document.addEventListener("visibilitychange", checkOnResume);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      offExpired();
      window.removeEventListener("focus", checkOnResume);
      document.removeEventListener("visibilitychange", checkOnResume);
    };
  }, [token, isAuthenticated, refresh]);

  return null;
};
